import {
  IntegrationAuthSchema,
  InstallableIntegrationSchema,
} from '../definitions/schemas';

export enum ConnectionEvents {
  Connect = 'connect',
}

export enum ConnectionResult {
  Error = 'error',
  Success = 'success',
}

export type ConnectEventDetail = {
  auth: IntegrationAuthSchema;
  message?: string;
  provider: InstallableIntegrationSchema;
  result: ConnectionResult;
};

const eventManager = new EventTarget();

class IntegrationService {
  public static authWindow: Window | null = null;

  public static provider: InstallableIntegrationSchema | null = null;

  public static configure() {
    // file deepcode ignore InsufficientPostmessageValidation: multiple origins, we can't validate for just one
    window.addEventListener('message', (event: MessageEvent) => {
      if (IntegrationService.authWindow && IntegrationService.provider) {
        IntegrationService.authWindow.close();
        IntegrationService.authWindow = null;

        const { message, result, ...auth } = event.data;

        eventManager.dispatchEvent(
          new CustomEvent<ConnectEventDetail>(ConnectionEvents.Connect, {
            detail: {
              auth,
              message,
              provider: IntegrationService.provider,
              result,
            },
          })
        );

        IntegrationService.provider = null;
      }
    });
  }

  public static connect(provider: InstallableIntegrationSchema) {
    /**
     * window.top defines the outermost frame which sometimes is not DSR,
     * so we return the current window.
     */
    const currentWindow = window.self;
    if (!currentWindow) return;

    if (IntegrationService.authWindow?.closed) {
      IntegrationService.authWindow = null;
      IntegrationService.provider = null;
    }

    if (IntegrationService.authWindow) {
      // Do NOT update IntegrationService.provider
      IntegrationService.authWindow.focus();
      return;
    }

    const height = 560; // theme.spacing(70);
    const width = 480; // theme.spacing(60);

    const left =
      currentWindow.outerWidth / 2 + currentWindow.screenX - width / 2;
    const top =
      currentWindow.outerHeight / 2 + currentWindow.screenY - height / 2;

    const { installUrl } = provider;
    if (!installUrl) {
      return;
    }

    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/open
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
     */
    IntegrationService.authWindow = window.open(
      installUrl,
      `Ref_${btoa(installUrl)}`,
      `height=${height},left=${left},top=${top},width=${width}`
    );

    IntegrationService.provider = provider;
  }

  public static on(
    eventName: ConnectionEvents,
    callback: EventListenerOrEventListenerObject
  ) {
    eventManager.addEventListener(eventName, callback);
  }

  public static off(
    eventName: ConnectionEvents,
    callback: EventListenerOrEventListenerObject
  ) {
    eventManager.removeEventListener(eventName, callback);
  }
}

export default IntegrationService;
